/**
 * 展示层格式化：分数、百分位、时间、学校/技能显示名。
 * 注意：这里**不做任何分数计算**，只把后端 MatchResult 里的数字变成文案。
 */
import { SCHOOL_MAP, SKILL_MAP } from '@crossmatch/shared';
import type { SchoolId } from '@crossmatch/shared';

const dateFmt = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const dateTimeFmt = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const relativeFmt = new Intl.RelativeTimeFormat('zh-CN', { numeric: 'auto' });

/** 排序分：整数不带小数点，小数保留 1 位（后端已按 1 位小数返回） */
export function formatScore(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** 0–1 的分量 → 百分比整数 */
export function formatRatio(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${Math.round(clamp01(value) * 100)}%`;
}

/** 0–100 的池内百分位 */
export function formatPercentile(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return String(Math.round(value));
}

/** 权重（0–1）→ 百分比文案 */
export function formatWeight(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${Math.round(clamp01(value) * 100)}%`;
}

export function formatHours(hours: number | null | undefined): string {
  if (typeof hours !== 'number' || !Number.isFinite(hours)) return '未填写';
  return `${hours} 小时/周`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '时间无法识别';
  return dateFmt.format(d);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '时间无法识别';
  return dateTimeFmt.format(d);
}

/** 截止时间：过期时明确标出，避免用户误判 */
export function formatDue(iso: string | null | undefined): string {
  if (!iso) return '未设置截止时间';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '截止时间无法识别';
  return `${formatDate(iso)}${d.getTime() < Date.now() ? '（已逾期）' : ''}`;
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '时间无法识别';
  const diffMs = d.getTime() - Date.now();
  const minutes = diffMs / 60_000;
  if (Math.abs(minutes) < 1) return '刚刚';
  if (Math.abs(minutes) < 60) return relativeFmt.format(Math.round(minutes), 'minute');
  const hours = diffMs / 3_600_000;
  if (Math.abs(hours) < 24) return relativeFmt.format(Math.round(hours), 'hour');
  const days = diffMs / 86_400_000;
  if (Math.abs(days) < 31) return relativeFmt.format(Math.round(days), 'day');
  return formatDate(iso);
}

/** 学校简称（如「上外」）；未知 id 原样返回，便于发现后端词表漂移 */
export function schoolLabel(id: string | null | undefined): string {
  if (!id) return '未知学校';
  return SCHOOL_MAP[id as SchoolId]?.short ?? `${id}（未收录）`;
}

export function schoolFullLabel(id: string | null | undefined): string {
  if (!id) return '未知学校';
  return SCHOOL_MAP[id as SchoolId]?.name ?? `${id}（未收录）`;
}

/** 技能中文名；未知 id 原样返回并标注，避免静默显示成空 */
export function skillLabel(id: string | null | undefined): string {
  if (!id) return '未知技能';
  return SKILL_MAP[id]?.name ?? `${id}（未收录）`;
}

export function skillListLabel(ids: string[] | null | undefined, separator = '、'): string {
  if (!ids || ids.length === 0) return '—';
  return ids.map((id) => skillLabel(id)).join(separator);
}

export function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

export function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

/** 文件名安全的时间戳（导出用） */
export function fileStamp(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(
    date.getMinutes(),
  )}`;
}